/**
 * src/modules/connect/services/meetingSessionService.js
 *
 * Client service for establishing, renewing, and verifying meeting participant sessions.
 */

import axios from "axios";
import { CONNECT_BASE } from "../config/connectConfig";
import {
  getMeetingSession,
  getMeetingToken,
  saveMeetingSession,
  clearMeetingSession,
  isMeetingSessionValid,
} from "../lib/meetingSession";
import { authService } from "../../../utils/auth";
import logger from "../../../utils/logger";

export const sessionHttp = axios.create({
  baseURL: CONNECT_BASE,
});

let inFlightJoinPromises = new Map();
let inFlightRefreshPromises = new Map();

export const meetingSessionService = {
  /**
   * Establish a meeting session via POST /connect/meetings/{meeting_id}/join-session.
   */
  async joinSession(meetingId, explicitToken = null) {
    if (!meetingId) throw new Error("Missing meetingId");

    if (inFlightJoinPromises.has(meetingId)) {
      return inFlightJoinPromises.get(meetingId);
    }

    const promise = (async () => {
      try {
        let token = explicitToken || authService.getToken();

        // 1. If token is missing, attempt proactive auth refresh before dispatching request
        if (!token && !explicitToken && (authService.hasRefreshToken() || Boolean(authService.getUser()))) {
          try {
            logger.info("[MeetingSessionService] Access token missing in memory; attempting auth refresh before join-session...");
            token = await authService.refreshToken();
          } catch (refreshErr) {
            logger.warn("[MeetingSessionService] Proactive auth token refresh failed:", refreshErr?.message || refreshErr);
          }
        }

        // 2. NEVER send an unauthenticated join request
        if (!token) {
          const authErr = new Error("Authentication session is not ready. Please sign in again.");
          authErr.status = 401;
          logger.warn("[MeetingSessionService] Cannot join meeting: No main application access token available.");
          throw authErr;
        }

        logger.info("[MeetingSessionService] Calling join-session for meeting:", meetingId);
        let res;
        try {
          res = await sessionHttp.post(
            `/connect/meetings/${meetingId}/join-session`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (postErr) {
          const status = postErr?.response?.status;
          const detail = postErr?.response?.data?.detail;
          const isAuthChallenge =
            status === 401 ||
            (status === 403 && typeof detail === "string" && detail.trim().toLowerCase() === "not authenticated");

          // 3. Handle authentication expiration with exactly one retry (never for authorization rejections)
          if (isAuthChallenge && !explicitToken && (authService.hasRefreshToken() || Boolean(authService.getUser()))) {
            logger.info("[MeetingSessionService] join-session received authentication challenge (401/403 Not authenticated). Refreshing and retrying once...");
            try {
              token = await authService.refreshToken();
              if (token) {
                res = await sessionHttp.post(
                  `/connect/meetings/${meetingId}/join-session`,
                  {},
                  { headers: { Authorization: `Bearer ${token}` } }
                );
              } else {
                throw postErr;
              }
            } catch (retryErr) {
              logger.error("[MeetingSessionService] Token refresh retry failed:", retryErr?.message || retryErr);
              throw retryErr;
            }
          } else {
            throw postErr;
          }
        }

        if (res?.data?.meeting_token) {
          const saved = saveMeetingSession(meetingId, res.data);
          return saved;
        }
        throw new Error("Invalid join session response");
      } catch (err) {
        logger.error("[MeetingSessionService] joinSession failed:", err?.message || err);
        throw err;
      } finally {
        inFlightJoinPromises.delete(meetingId);
      }
    })();

    inFlightJoinPromises.set(meetingId, promise);
    return promise;
  },

  /**
   * Renew the meeting-scoped credential via POST /connect/meetings/{meeting_id}/refresh-session.
   */
  async refreshSession(meetingId) {
    if (!meetingId) throw new Error("Missing meetingId");

    if (inFlightRefreshPromises.has(meetingId)) {
      return inFlightRefreshPromises.get(meetingId);
    }

    const promise = (async () => {
      try {
        const currentMeetingToken = getMeetingToken(meetingId);
        if (!currentMeetingToken) {
          throw new Error("No existing meeting token available for renewal");
        }

        logger.info("[MeetingSessionService] Calling refresh-session for meeting:", meetingId);
        const res = await sessionHttp.post(
          `/connect/meetings/${meetingId}/refresh-session`,
          {},
          {
            headers: {
              Authorization: `Bearer ${currentMeetingToken}`,
            },
          }
        );

        if (res.data?.meeting_token) {
          const saved = saveMeetingSession(meetingId, res.data);
          logger.info("[MeetingSessionService] Successfully renewed meeting session for:", meetingId);
          return saved;
        }
        throw new Error("Invalid refresh session response");
      } catch (err) {
        logger.error("[MeetingSessionService] refreshSession failed:", err?.message || err);
        throw err;
      } finally {
        inFlightRefreshPromises.delete(meetingId);
      }
    })();

    inFlightRefreshPromises.set(meetingId, promise);
    return promise;
  },

  /**
   * Ensure an active meeting session exists (returning cached if valid, or joining).
   */
  async ensureSession(meetingId) {
    if (!meetingId) return null;
    if (isMeetingSessionValid(meetingId)) {
      return getMeetingSession(meetingId);
    }
    return this.joinSession(meetingId);
  },

  clearSession(meetingId) {
    clearMeetingSession(meetingId);
  },
};

export default meetingSessionService;
